"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type BrandDirectoryEntry = {
  name: string
  slug: string
}

const LETTERS = Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index))
const OTHER_KEY = "#"

const getLetterKey = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return OTHER_KEY
  }
  const firstChar = trimmed.charAt(0).toUpperCase()
  return /^[A-Z]$/.test(firstChar) ? firstChar : OTHER_KEY
}

type BrandDirectoryProps = {
  brands: BrandDirectoryEntry[]
}

const sortByName = (a: BrandDirectoryEntry, b: BrandDirectoryEntry) =>
  a.name.localeCompare(b.name, "es", { sensitivity: "base" })

export default function BrandDirectory({ brands }: BrandDirectoryProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeLetter, setActiveLetter] = useState<string | null>(null)

  const sortedBrands = useMemo(() => {
    return [...brands].sort(sortByName)
  }, [brands])

  const filteredBrands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      return sortedBrands
    }
    return sortedBrands.filter((brand) => brand.name.toLowerCase().includes(term))
  }, [sortedBrands, searchTerm])

  const sections = useMemo(() => {
    const groups = new Map<string, BrandDirectoryEntry[]>()
    LETTERS.forEach((letter) => {
      groups.set(letter, [])
    })
    groups.set(OTHER_KEY, [])

    filteredBrands.forEach((brand) => {
      const key = getLetterKey(brand.name)
      const collection = groups.get(key)
      if (collection) {
        collection.push(brand)
      }
    })

    return [...LETTERS, OTHER_KEY]
      .map((letter) => {
        const entries = groups.get(letter) ?? []
        return {
          letter,
          entries: [...entries].sort(sortByName),
        }
      })
      .filter((section) => section.entries.length > 0)
  }, [filteredBrands])

  const availableLetters = useMemo(() => new Set(sections.map((section) => section.letter)), [sections])

  useEffect(() => {
    if (activeLetter && !availableLetters.has(activeLetter)) {
      setActiveLetter(null)
    }
  }, [activeLetter, availableLetters])

  const handleLetterClick = useCallback(
    (letter: string) => {
      if (!availableLetters.has(letter)) {
        return
      }

      setActiveLetter((current) => {
        const next = current === letter ? null : letter
        if (next) {
          const elementId = letter === OTHER_KEY ? "letter-other" : `letter-${letter}`
          const anchor = document.getElementById(elementId)
          if (anchor) {
            anchor.scrollIntoView({ behavior: "smooth", block: "start" })
          }
        }
        return next
      })
    },
    [availableLetters]
  )

  return (
    <div className="space-y-8">
      <header className="space-y-4">
        <h1 className="text-3xl font-semibold tracking-tight text-ui-fg-base">Marcas</h1>
        <p className="max-w-3xl text-base text-ui-fg-subtle">
          Explorá nuestras marcas destacadas y descubrí productos artesanales seleccionados por expertos.
        </p>
        <div className="flex flex-wrap items-center gap-2 text-sm uppercase tracking-wide text-ui-fg-subtle">
          {[...LETTERS, OTHER_KEY].map((letter) => {
            const isDisabled = !availableLetters.has(letter)
            const isActive = activeLetter === letter
            return (
              <button
                key={letter}
                type="button"
                onClick={() => handleLetterClick(letter)}
                disabled={isDisabled}
                className={`rounded-full border px-3 py-1 transition-colors ${
                  isDisabled
                    ? "cursor-not-allowed border-transparent text-ui-fg-muted"
                    : isActive
                    ? "border-ui-border-strong bg-ui-bg-inverted text-ui-fg-on-inverted"
                    : "border-transparent hover:border-ui-border-base hover:text-ui-fg-base"
                }`}
                aria-disabled={isDisabled}
              >
                {letter === OTHER_KEY ? "#" : letter}
              </button>
            )
          })}
        </div>
      </header>

      <div className="max-w-xl">
        <label className="block text-sm font-medium text-ui-fg-subtle" htmlFor="brand-directory-search">
          Buscar marcas
        </label>
        <input
          id="brand-directory-search"
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Ingresa el nombre de la marca"
          className="mt-2 w-full rounded-full border border-ui-border-base bg-ui-bg-field px-4 py-2 text-base text-ui-fg-base focus:outline-none focus:ring-2 focus:ring-ui-border-strong"
        />
      </div>

      {sections.length === 0 ? (
        <p className="text-base text-ui-fg-subtle">No se encontraron marcas para tu búsqueda.</p>
      ) : (
        <div className="space-y-10">
          {sections.map((section) => (
            <section
              key={section.letter}
              id={section.letter === OTHER_KEY ? "letter-other" : `letter-${section.letter}`}
              className="scroll-mt-24"
            >
              <h2 className="mb-4 text-xl font-semibold text-ui-fg-base">
                {section.letter === OTHER_KEY ? "#" : section.letter}
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {section.entries.map((brand) => (
                  <li key={brand.slug}>
                    <LocalizedClientLink
                      href={`/${brand.slug}`}
                      className="text-base text-ui-fg-base transition-colors hover:text-ui-fg-interactive"
                    >
                      {brand.name}
                    </LocalizedClientLink>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
